import { useEffect, useRef, useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { Save, Loader2 } from 'lucide-react';
import { HelpCenterCardData } from './HelpCenterCard';
import { supabase } from '@/lib/supabaseClient';

// TipTap imports
import { EditorContent, useEditor } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import Link from '@tiptap/extension-link';
import Image from '@tiptap/extension-image';
import Placeholder from '@tiptap/extension-placeholder';
import Heading from '@tiptap/extension-heading';
import { Table } from '@tiptap/extension-table';
import TableRow from '@tiptap/extension-table-row';
import TableHeader from '@tiptap/extension-table-header';
import TableCell from '@tiptap/extension-table-cell';
import { Node, mergeAttributes, type CommandProps } from '@tiptap/core';

// Video extension (copied from PartnerHelpCenter)
declare module '@tiptap/core' {
  interface Commands<ReturnType> {
    video: {
      setVideo: (attrs: {
        src: string;
        title?: string;
        controls?: boolean;
        autoplay?: boolean;
        loop?: boolean;
        muted?: boolean;
        class?: string;
      }) => ReturnType;
    };
    loomVideo: {
      setLoomVideo: (attrs: {
        src: string;
        title?: string;
        class?: string;
      }) => ReturnType;
    };
  }
}

const Video = Node.create({
  name: 'video',
  group: 'block',
  atom: true,
  inline: false,
  draggable: true,
  selectable: true,
  addAttributes() {
    return {
      src: { default: null },
      controls: { default: true },
      autoplay: { default: false },
      loop: { default: false },
      muted: { default: false },
      title: { default: null },
      class: { default: 'max-w-full w-full rounded-md' },
    };
  },
  parseHTML() {
    return [{ tag: 'video' }];
  },
  renderHTML({ HTMLAttributes }) {
    return ['video', mergeAttributes(HTMLAttributes, { controls: true })];
  },
  addCommands() {
    return {
      setVideo:
        (attrs: {
          src: string;
          title?: string;
          controls?: boolean;
          autoplay?: boolean;
          loop?: boolean;
          muted?: boolean;
          class?: string;
        }) =>
        ({ chain }: CommandProps) => {
          if (!attrs?.src) return false;
          return chain()
            .insertContent({
              type: this.name,
              attrs,
            })
            .run();
        },
    };
  },
});

const LoomVideo = Node.create({
  name: 'loomVideo',
  group: 'block',
  atom: true,
  inline: false,
  draggable: true,
  selectable: true,
  addAttributes() {
    return {
      src: { default: null },
      title: { default: null },
    };
  },
  parseHTML() {
    return [
      {
        tag: 'div[data-loom-video]',
        getAttrs: (dom) => {
          const iframe = dom.querySelector('iframe');
          const src = iframe?.getAttribute('src');
          return src && src.includes('loom.com') ? { src } : null;
        }
      }
    ];
  },
  renderHTML({ HTMLAttributes }) {
    const src = HTMLAttributes.src;
    if (src && src.includes('loom.com')) {
      // Convert share URL to embed URL
      let embedSrc = src;
      if (src.includes('/share/')) {
        embedSrc = src.replace('/share/', '/embed/');
      }
      
      return ['div', {
        'data-loom-video': '',
        'class': 'loom-video-wrapper',
        'style': 'position: relative; padding-bottom: 62.5%; height: 0; margin: 1rem 0;'
      },
        ['iframe', {
          src: embedSrc,
          frameborder: '0',
          webkitallowfullscreen: 'true',
          mozallowfullscreen: 'true',
          allowfullscreen: 'true',
          style: 'position: absolute; top: 0; left: 0; width: 100%; height: 100%;'
        }]
      ];
    }
    return ['div', { class: 'text-red-500' }, 'Invalid Loom URL'];
  },
  addCommands() {
    return {
      setLoomVideo:
        (attrs: { src: string; title?: string }) =>
        ({ chain }: CommandProps) => {
          if (!attrs?.src || !attrs.src.includes('loom.com')) return false;
          return chain()
            .insertContent({
              type: this.name,
              attrs: { src: attrs.src, title: attrs.title },
            })
            .run();
        },
    };
  },
});

interface HelpCenterEditModalProps {
  isOpen: boolean;
  onClose: () => void;
  card: HelpCenterCardData | null;
  onSave: (cardData: { title: string; description?: string; content: string; is_published: boolean }) => Promise<void>;
  isLoading?: boolean;
}

function HelpCenterEditModal({ isOpen, onClose, card, onSave, isLoading = false }: HelpCenterEditModalProps) {
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [isPublished, setIsPublished] = useState(true);
  const imageInputRef = useRef<HTMLInputElement | null>(null);
  const videoInputRef = useRef<HTMLInputElement | null>(null);

  const editorRef = useRef<any>(null);

  const editor = useEditor({
    extensions: [
      StarterKit.configure({
        bulletList: { keepMarks: true },
        orderedList: { keepMarks: true },
        heading: false,
      }),
      Heading.configure({
        levels: [1, 2, 3, 4, 5, 6],
      }),
      Link.configure({
        autolink: true,
        openOnClick: true,
        linkOnPaste: true,
      }),
      Image.configure({
        HTMLAttributes: {
          class: 'max-w-full h-auto rounded-md',
        },
      }),
      Video,
      LoomVideo,
      Placeholder.configure({
        placeholder: 'Write your Help Center content… Headings, lists, tables, links, images supported.',
      }),
      Table.configure({
        resizable: true,
        lastColumnResizable: true
      }),
      TableRow,
      TableHeader,
      TableCell,
    ],
    content: '',
    autofocus: 'start',
    editable: true,
    editorProps: {
      attributes: {
        class:
          'prose prose-sm max-w-none focus:outline-none min-h-[300px] px-3 py-2 border rounded-md dark:prose-invert prose-headings:text-foreground prose-p:text-foreground prose-strong:text-foreground prose-em:text-foreground prose-a:text-primary prose-code:text-foreground prose-pre:bg-muted prose-pre:text-foreground prose-blockquote:text-foreground prose-ol:text-foreground prose-ul:text-foreground prose-li:marker:text-foreground prose-table:text-foreground prose-th:text-foreground prose-td:text-foreground tiptap-content text-foreground',
      },
      handlePaste: (_, event) => {
        const text = event.clipboardData?.getData('text/plain');
        if (text && text.trim().includes('loom.com')) {
          event.preventDefault();
          if (editorRef.current) {
            editorRef.current.chain().focus().setLoomVideo({ src: text.trim() }).run();
          }
          return true;
        }
        return false;
      },
    },
  });

  useEffect(() => {
    if (editor) {
      editorRef.current = editor;
    }
  }, [editor]);

  // Initialize form when card changes
  useEffect(() => {
    if (card && editor) {
      setTitle(card.title);
      setDescription(card.description || '');
      setIsPublished(card.is_published);
      editor.commands.setContent(card.content || '');
    } else if (!card) {
      // Reset form for new card
      setTitle('');
      setDescription('');
      setIsPublished(true);
      if (editor) {
        editor.commands.setContent('');
      }
    }
  }, [card, editor]);

  const getMarkdown = (): string => {
    if (!editor) return '';
    return editor.getHTML();
  };

  const handleSave = async () => {
    if (!title.trim()) return;

    const content = getMarkdown();
    await onSave({
      title: title.trim(),
      description: description.trim() || undefined,
      content,
      is_published: isPublished,
    });
  };

  const uploadAsset = async (file: File, kind: 'image' | 'video') => {
    if (!editor) return;

    try {
      // Get session using supabase client (correct way)
      const { data: sessionData } = await supabase.auth.getSession();
      const token = sessionData?.session?.access_token;
      
      if (!token) {
        console.error('No auth token');
        return;
      }

      const formData = new FormData();
      formData.append('file', file);

      const response = await fetch(`${import.meta.env.VITE_SUPABASE_URL}/functions/v1/partner-branding?action=upload-media`, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${token}`,
        },
        body: formData,
      });

      if (!response.ok) {
        console.error('Upload failed');
        return;
      }

      const data = await response.json();
      if (!data?.url) {
        console.error('No URL in response');
        return;
      }

      editor.chain().focus();
      if (kind === 'image') {
        editor
          .chain()
          .focus()
          .setImage({ src: data.url, alt: file.name || 'image' })
          .run();
      } else {
        editor
          .chain()
          .focus()
          .setVideo({ src: data.url, title: file.name || 'video' })
          .run();
      }
    } catch (error) {
      console.error('Upload error:', error);
    }
  };

  // Toolbar component
  const ToolbarButton = ({
    onClick,
    active = false,
    children,
  }: {
    onClick: () => void;
    active?: boolean;
    children: React.ReactNode;
  }) => (
    <Button
      type="button"
      variant={active ? 'secondary' : 'ghost'}
      size="sm"
      onClick={onClick}
      className="h-8 w-8 p-0"
    >
      {children}
    </Button>
  );

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-6xl max-h-[90vh] flex flex-col">
        <DialogHeader>
          <DialogTitle>{card ? 'Edit Help Center Card' : 'Create Help Center Card'}</DialogTitle>
        </DialogHeader>

        <div className="flex flex-col flex-1 min-h-0 space-y-4">
          {/* Title input and Published toggle in same row */}
          <div className="flex items-end gap-4">
            <div className="flex-1">
              <Label htmlFor="title">Title</Label>
              <Input
                id="title"
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                placeholder="Enter card title..."
                className="mt-1"
              />
            </div>
            <div className="flex items-center space-x-2">
              <Switch
                id="published"
                checked={isPublished}
                onCheckedChange={setIsPublished}
              />
              <Label htmlFor="published">Published</Label>
            </div>
          </div>

          {/* Description input */}
          <div>
            <Label htmlFor="description">Description</Label>
            <Input
              id="description"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="Enter card description..."
              className="mt-1"
            />
          </div>

          {/* Toolbar */}
          <div className="flex flex-wrap items-center gap-1 border rounded-md p-2 bg-muted/40">
            <ToolbarButton
              onClick={() => editor?.chain().focus().toggleBold().run()}
              active={!!editor?.isActive('bold')}
            >
              <strong>B</strong>
            </ToolbarButton>
            <ToolbarButton
              onClick={() => editor?.chain().focus().toggleItalic().run()}
              active={!!editor?.isActive('italic')}
            >
              <em>I</em>
            </ToolbarButton>
            <div className="w-px h-6 bg-border mx-1" />
            <ToolbarButton
              onClick={() => editor?.chain().focus().toggleHeading({ level: 1 }).run()}
              active={!!editor?.isActive('heading', { level: 1 })}
            >
              H1
            </ToolbarButton>
            <ToolbarButton
              onClick={() => editor?.chain().focus().toggleHeading({ level: 2 }).run()}
              active={!!editor?.isActive('heading', { level: 2 })}
            >
              H2
            </ToolbarButton>
            <ToolbarButton
              onClick={() => editor?.chain().focus().toggleBlockquote().run()}
              active={!!editor?.isActive('blockquote')}
            >
              "
            </ToolbarButton>
            <div className="w-px h-6 bg-border mx-1" />
            <ToolbarButton
              onClick={() => editor?.chain().focus().toggleBulletList().run()}
              active={!!editor?.isActive('bulletList')}
            >
              •
            </ToolbarButton>
            <ToolbarButton
              onClick={() => editor?.chain().focus().toggleOrderedList().run()}
              active={!!editor?.isActive('orderedList')}
            >
              1.
            </ToolbarButton>
            <ToolbarButton
              onClick={() => editor?.chain().focus().toggleCodeBlock().run()}
              active={!!editor?.isActive('codeBlock')}
            >
              {'</>'}
            </ToolbarButton>
            <ToolbarButton
              onClick={() =>
                editor
                  ?.chain()
                  .focus()
                  .insertTable({ rows: 3, cols: 3, withHeaderRow: true })
                  .run()
              }
            >
              ⊞
            </ToolbarButton>
            <div className="w-px h-6 bg-border mx-1" />
            <Button
              type="button"
              variant="ghost"
              size="sm"
              onClick={() => imageInputRef.current?.click()}
              className="h-8 w-8 p-0"
            >
              🖼️
            </Button>
            <Button
              type="button"
              variant="ghost"
              size="sm"
              onClick={() => videoInputRef.current?.click()}
              className="h-8 w-8 p-0"
            >
              🎥
            </Button>
          </div>

          {/* Hidden file inputs */}
          <input
            ref={imageInputRef}
            type="file"
            className="hidden"
            accept="image/*"
            onChange={async (e) => {
              const file = e.target.files?.[0];
              if (file) await uploadAsset(file, 'image');
              if (imageInputRef.current) imageInputRef.current.value = '';
            }}
          />
          <input
            ref={videoInputRef}
            type="file"
            className="hidden"
            accept="video/*"
            onChange={async (e) => {
              const file = e.target.files?.[0];
              if (file) await uploadAsset(file, 'video');
              if (videoInputRef.current) videoInputRef.current.value = '';
            }}
          />

          {/* Editor */}
          <div className="flex-1 min-h-0 border rounded-md overflow-y-auto">
            <EditorContent editor={editor} />
          </div>

          {/* Actions */}
          <div className="flex justify-end gap-2 pt-4">
            <Button variant="outline" onClick={onClose}>
              Cancel
            </Button>
            <Button onClick={handleSave} disabled={isLoading || !title.trim()}>
              {isLoading ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Saving...
                </>
              ) : (
                <>
                  <Save className="h-4 w-4 mr-2" />
                  Save
                </>
              )}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

export default HelpCenterEditModal;